import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Form, Select, Spin } from 'antd'
import type { FormInstance, TreeDataNode } from 'antd'
import { Input } from '@/components/ui/input'
import { PermissionGrid } from './PermissionGrid'
import { getPermissionCategories } from '@/api/permissions.api'
import { fetchUnits } from '@/api/units.api'
import { useAtomValue } from 'jotai'
import { currentUserAtom, permissionsAtom } from '@/store/auth.atom'

export interface PermissionFormValues {
  id?: string
  code?: string
  name: string
  appliedUnit: string[]
  note?: string
  permissions: React.Key[]
}

interface PermissionFormProps {
  form: FormInstance<PermissionFormValues>
  onSubmit: (values: PermissionFormValues) => void
  unitFieldDisabled?: boolean
}

const PILL_INPUT =
  '!bg-white disabled:!bg-gray-100 disabled:hover:!bg-gray-100 disabled:!text-gray-500 disabled:!cursor-not-allowed'
const TEXTAREA_INPUT = 'rounded-2xl py-3 !bg-white disabled:!cursor-not-allowed'

export function PermissionForm({
  form,
  onSubmit,
  unitFieldDisabled,
}: PermissionFormProps) {
  const permissionUser = useAtomValue(permissionsAtom)
  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1
  const shouldShowUnitField = showUnitColumn && unitFieldDisabled

  const { data: categories, isLoading: isCategoriesLoading } = useQuery({
    queryKey: ['permission-categories'],
    queryFn: ({ signal }) => getPermissionCategories(signal),
    refetchOnMount: 'always',
    staleTime: 5 * 60 * 1000,
  })

  // Fetch units for the select (fetch a large page to get all active units)
  const { data: unitData, isLoading: isUnitsLoading } = useQuery({
    queryKey: ['all-units'],
    queryFn: ({ signal }) => fetchUnits(0, 1000, { status: 1 }, signal),
    enabled: shouldShowUnitField,
    refetchOnMount: 'always',
    staleTime: 5 * 60 * 1000,
  })

  const unitOptions = useMemo(() => {
    return (
      unitData?.items.map((u) => ({
        value: u.id,
        label: u.name,
      })) || []
    )
  }, [unitData])

  const treeData: TreeDataNode[] = useMemo(() => {
    if (!categories) return []
    const filteredCategories = categories
      .map((cat) => {
        const filteredPermissions = cat.permissionList.filter((p) => {
          if (!showUnitColumn) {
            return permissionUser.includes(p.code)
          }
          return true
        })
        return {
          ...cat,
          permissionList: filteredPermissions,
        }
      })
      .filter((cat) => cat.permissionList.length > 0)

    if (filteredCategories.length === 0) return []

    return [
      {
        title: 'Tất cả',
        key: 'all',
        children: filteredCategories.map((cat) => ({
          title: cat.name,
          key: cat.id,
          children: [...cat.permissionList].map((p) => ({
            title: p.name,
            key: p.id,
          })),
        })),
      },
    ]
  }, [categories, showUnitColumn, permissionUser])

  const handleFinish = (values: PermissionFormValues) => {
    const trimmedValues = Object.fromEntries(
      Object.entries(values).map(([key, val]) => [
        key,
        typeof val === 'string' ? val.trim() : val,
      ]),
    ) as PermissionFormValues

    onSubmit(trimmedValues)
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={{ permissions: [], appliedUnit: [] }}
      requiredMark={(label, info) => (
        <>
          {label}
          {info.required && <span className="text-destructive ml-1">*</span>}
        </>
      )}
      className="space-y-6"
    >
      <div className="rounded-xl border border-solid border-gray-300 px-6 py-4">
        <h3 className="mb-4 text-lg font-bold">Thông tin chung</h3>

        <Form.Item
          name="name"
          label="Tên nhóm quyền"
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Vui lòng nhập tên nhóm quyền',
            },
          ]}
        >
          <Input
            placeholder="Nhập tên nhóm quyền"
            size="large"
            className={PILL_INPUT}
          />
        </Form.Item>

        {shouldShowUnitField ? (
          <Form.Item
            name="appliedUnit"
            label="Đơn vị áp dụng"
            rules={[
              { required: true, message: 'Vui lòng chọn đơn vị áp dụng' },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Chọn đơn vị"
              size="large"
              options={unitOptions}
              loading={isUnitsLoading}
              disabled={unitFieldDisabled}
              allowClear
              maxTagCount="responsive"
              className="w-full bg-white"
            />
          </Form.Item>
        ) : null}

        <Form.Item name="note" label="Ghi chú">
          <Input.TextArea
            placeholder="Nhập ghi chú"
            size="large"
            autoSize={{ minRows: 4, maxRows: 6 }}
            className={TEXTAREA_INPUT}
          />
        </Form.Item>
      </div>

      <div className="rounded-xl border border-solid border-gray-300 px-6 py-4">
        <h3 className="mb-4 text-lg font-bold">Phân quyền</h3>
        <Spin spinning={isCategoriesLoading}>
          <Form.Item name="permissions">
            {treeData.length > 0 ? (
              <PermissionGrid treeData={treeData} />
            ) : (
              <div className="py-4 text-gray-500">
                Chưa có dữ liệu phân quyền hoặc tài khoản không có quyền thao
                tác
              </div>
            )}
          </Form.Item>
        </Spin>
      </div>
    </Form>
  )
}
